import { useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import type {
    SupportEmailRequestListItemDto,
    SupportEmailRequestListFilters,
} from '../types/support-email-request.types';

type Pagination = {
    skip: number;
    take: number;
};

type Props = {
    searchText: string;
    pagination: Pagination;
    filters?: SupportEmailRequestListFilters;
};

export const useSupportEmailRequestsListApi = ({ searchText, pagination, filters }: Props) => {
    const axiosInstance = useAxiosAuth();

    const { data, isLoading, error } = useQuery<{
        items: SupportEmailRequestListItemDto[];
        totalCount: number;
    }>({
        queryKey: [
            ApiConfig.queries.superAdmin.supportEmailRequests.queryKeys.list,
            searchText,
            pagination,
            filters,
        ],
        queryFn: async () => {
            const params: Record<string, string | number | boolean | undefined> = {
                skip: pagination.skip,
                take: pagination.take,
                searchText: searchText || undefined,
            };

            if (filters?.status != null) params.status = filters.status;
            if (filters?.category != null) params.category = filters.category;
            if (filters?.isSuspicious != null) params.isSuspicious = filters.isSuspicious;
            if (filters?.from) params.from = filters.from;
            if (filters?.to) params.to = filters.to;

            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.superAdmin.supportEmailRequests.endpoints.list,
                params,
            );
            const response = await axiosInstance.get<{
                items: SupportEmailRequestListItemDto[];
                totalCount: number;
            }>(url);
            return response.data;
        },
    });

    return { list: data, isLoading, error };
};
